import { isExperimentPreview, isPreviewQuery } from './preview'
import config from 'constants/config'
import { ChertoExperiments, type ChertoExperimentConfig } from 'constants/experiments'
import {
  get as getLocalStorage,
  set as setLocalStorage,
} from 'lib/storage/isomorphicLocalStorage'
import { isRecord } from 'lib/object/objectUtils'
import { canAccessExperimentPanel } from './experimentPanelAccess'

const STORAGE_KEY_PREFIX = 'ExperimentControlPanel:overrides'
const STORAGE_VERSION = 1

export interface ExperimentPanelOverrides {
  readonly optimizelyExperiments: Record<string, string>
  readonly optimizelyFeatureFlags: Record<string, boolean>
  readonly chertoExperiments: Record<string, string>
  readonly labFeatureFlags: Record<string, boolean>
  readonly labRemoteConfigFlags: Record<string, App.LabRemoteConfigResult['value']>
}

interface PersistedExperimentPanelState {
  readonly version: number
  readonly enabled: boolean
  readonly overrides: ExperimentPanelOverrides
}

const emptyOverrides = (): ExperimentPanelOverrides => ({
  optimizelyExperiments: {},
  optimizelyFeatureFlags: {},
  chertoExperiments: {},
  labFeatureFlags: {},
  labRemoteConfigFlags: {},
})

const defaultPersistenceState = (): PersistedExperimentPanelState => ({
  version: STORAGE_VERSION,
  enabled: true,
  overrides: emptyOverrides(),
})

// Changes made while persistence is disabled remain available for this page,
// and become persisted if the staff member enables persistence again.
let sessionOverrides = emptyOverrides()

function getStorageKey(): string {
  return `${STORAGE_KEY_PREFIX}:${config.BRAND}`
}

function getRecord<T>(value: unknown, isValue: (value: unknown) => value is T): Record<string, T> {
  if (!isRecord(value)) return {}

  return Object.fromEntries(
    Object.entries(value).filter((entry): entry is [string, T] => isValue(entry[1])),
  )
}

function getStringRecord(value: unknown): Record<string, string> {
  return getRecord(value, (value): value is string => typeof value === 'string')
}

function getBooleanRecord(value: unknown): Record<string, boolean> {
  return getRecord(value, (value): value is boolean => typeof value === 'boolean')
}

function isLabRemoteConfigValue(value: unknown): value is App.LabRemoteConfigResult['value'] {
  if (value === null) return true

  const valueType = typeof value
  return valueType === 'boolean' ||
    valueType === 'number' ||
    valueType === 'string' ||
    Array.isArray(value) ||
    isRecord(value)
}

function getLabRemoteConfigValueRecord(value: unknown): Record<string, App.LabRemoteConfigResult['value']> {
  return getRecord(value, isLabRemoteConfigValue)
}

function parsePersistenceState(value: unknown): PersistedExperimentPanelState {
  if (!value || typeof value !== 'object') return defaultPersistenceState()

  const persistedState = value as Partial<PersistedExperimentPanelState>
  if (persistedState.version !== STORAGE_VERSION) return defaultPersistenceState()

  const overrides = persistedState.overrides
  return {
    version: STORAGE_VERSION,
    enabled: persistedState.enabled !== false,
    overrides: {
      optimizelyExperiments: getStringRecord(overrides?.optimizelyExperiments),
      optimizelyFeatureFlags: getBooleanRecord(overrides?.optimizelyFeatureFlags),
      chertoExperiments: getStringRecord(overrides?.chertoExperiments),
      labFeatureFlags: getBooleanRecord(overrides?.labFeatureFlags),
      labRemoteConfigFlags: getLabRemoteConfigValueRecord(overrides?.labRemoteConfigFlags),
    },
  }
}

export function getExperimentPanelPersistence(): PersistedExperimentPanelState {
  return parsePersistenceState(getLocalStorage(getStorageKey()))
}

function storeExperimentPanelPersistence(persistence: PersistedExperimentPanelState): void {
  setLocalStorage(getStorageKey(), persistence)
}

function updatePersistence(update: (persistence: PersistedExperimentPanelState) => PersistedExperimentPanelState): void {
  storeExperimentPanelPersistence(update(getExperimentPanelPersistence()))
}

export function setExperimentPanelPersistenceEnabled(enabled: boolean): void {
  updatePersistence(persistence => ({
    ...persistence,
    enabled,
    overrides: enabled ? {
      optimizelyExperiments: { ...persistence.overrides.optimizelyExperiments, ...sessionOverrides.optimizelyExperiments },
      optimizelyFeatureFlags: { ...persistence.overrides.optimizelyFeatureFlags, ...sessionOverrides.optimizelyFeatureFlags },
      chertoExperiments: { ...persistence.overrides.chertoExperiments, ...sessionOverrides.chertoExperiments },
      labFeatureFlags: { ...persistence.overrides.labFeatureFlags, ...sessionOverrides.labFeatureFlags },
      labRemoteConfigFlags: { ...persistence.overrides.labRemoteConfigFlags, ...sessionOverrides.labRemoteConfigFlags },
    } : persistence.overrides,
  }))

  if (enabled) {
    sessionOverrides = emptyOverrides()
  }
}

export function clearExperimentPanelOverrides(): void {
  sessionOverrides = emptyOverrides()
  updatePersistence(persistence => ({ ...persistence, overrides: emptyOverrides() }))
}

type OverrideType = keyof ExperimentPanelOverrides

function updateOverride<T extends string | boolean | App.LabRemoteConfigResult['value']>(type: OverrideType, key: string, value: T): void {
  updatePersistence(persistence => {
    if (!persistence.enabled) {
      sessionOverrides = {
        ...sessionOverrides,
        [type]: {
          ...sessionOverrides[type],
          [key]: value,
        },
      }
      return persistence
    }

    const { [key]: _previousOverride, ...remainingOverrides } = persistence.overrides[type]
    const overrides = { ...remainingOverrides, [key]: value }

    return {
      ...persistence,
      overrides: {
        ...persistence.overrides,
        [type]: overrides,
      },
    }
  })
}

export function updateOptimizelyExperimentOverride(experimentId: string, variationKey: string): void {
  updateOverride('optimizelyExperiments', experimentId, variationKey)
}

export function updateOptimizelyFeatureFlagOverride(flagKey: string, enabled: boolean): void {
  updateOverride('optimizelyFeatureFlags', flagKey, enabled)
}

export function updateChertoExperimentOverride(experimentId: string, variationKey: string): void {
  updateOverride('chertoExperiments', experimentId, variationKey)
}

export function updateLabFeatureFlagOverride(flagKey: string, enabled: boolean): void {
  updateOverride('labFeatureFlags', flagKey, enabled)
}

export function updateLabRemoteConfigOverride(flagKey: string, value: App.LabRemoteConfigResult['value']): void {
  updateOverride('labRemoteConfigFlags', flagKey, value)
}

export function clearLabRemoteConfigOverride(flagKey: string): void {
  const { [flagKey]: _removedSessionOverride, ...remainingSessionOverrides } = sessionOverrides.labRemoteConfigFlags
  sessionOverrides = {
    ...sessionOverrides,
    labRemoteConfigFlags: remainingSessionOverrides,
  }

  updatePersistence(persistence => {
    const { [flagKey]: _removedPersistedOverride, ...remainingPersistedOverrides } = persistence.overrides.labRemoteConfigFlags

    return {
      ...persistence,
      overrides: {
        ...persistence.overrides,
        labRemoteConfigFlags: remainingPersistedOverrides,
      },
    }
  })
}

export function hasLabRemoteConfigOverride(flagKey: string): boolean {
  if (sessionOverrides.labRemoteConfigFlags[flagKey] !== undefined) {
    return true
  }

  return getExperimentPanelPersistence().overrides.labRemoteConfigFlags[flagKey] !== undefined
}

function hasSameValues<T>(left: Record<string, T>, right: Record<string, T>): boolean {
  const leftEntries = Object.entries(left)
  return leftEntries.length === Object.keys(right).length && leftEntries.every(([key, value]) => right[key] === value)
}

function isValidOptimizelyExperimentOverride(
  experiment: App.OptimizelyExperimentDetails,
  variationKey: string,
): boolean {
  const validVariationKeys = Object.keys(experiment.variation ?? {})
  return variationKey === 'off' || validVariationKeys.includes(variationKey) || (validVariationKeys.length === 0 && variationKey === 'on')
}

function getValidOptimizelyExperimentOverrides(
  experiments: App.OptimizelyState['optimizelyExperiments'],
  overrides: Record<string, string>,
): Record<string, string> {
  return Object.fromEntries(Object.entries(overrides).filter(([experimentId, variationKey]) => {
    const experiment = experiments[experimentId]
    // An experiment that has not been decided yet cannot invalidate a saved
    // override. Keep it until a decision explicitly confirms its variations.
    if (!experiment) return true

    return isValidOptimizelyExperimentOverride(experiment, variationKey)
  }))
}

function getValidChertoExperimentOverrides(overrides: Record<string, string>): Record<string, string> {
  const configuredExperimentIds = new Set(config.CHERTO_EXPERIMENTS ?? [])

  return Object.fromEntries(Object.entries(overrides).filter(([experimentId, variationKey]) => {
    const experiment = (Object.values(ChertoExperiments) as Array<ChertoExperimentConfig>).find(candidate => candidate.id === experimentId)
    return configuredExperimentIds.has(experimentId) && !!experiment && (variationKey === 'off' || experiment.variations.includes(variationKey))
  }))
}

function getValidBooleanOverrides(
  overrides: Record<string, boolean>,
  configuredKeys: Array<string> | undefined,
): Record<string, boolean> {
  const configuredKeySet = new Set(configuredKeys ?? [])
  return Object.fromEntries(Object.entries(overrides).filter(([key]) => configuredKeySet.has(key)))
}

function getValidLabRemoteConfigOverrides(
  overrides: Record<string, App.LabRemoteConfigResult['value']>,
  configuredKeys: Array<string> | undefined,
): Record<string, App.LabRemoteConfigResult['value']> {
  const configuredKeySet = new Set(configuredKeys ?? [])
  return Object.fromEntries(Object.entries(overrides).filter(([key, value]) => (
    configuredKeySet.has(key) && isLabRemoteConfigValue(value)
  )))
}

function getOverriddenOptimizelyExperiments(
  experiments: App.OptimizelyState['optimizelyExperiments'],
  overrides: Record<string, string>,
): App.OptimizelyState['optimizelyExperiments'] {
  return Object.entries(overrides).reduce<App.OptimizelyState['optimizelyExperiments']>((nextExperiments, [experimentId, variationKey]) => {
    const experiment = nextExperiments[experimentId]
    if (!experiment) return nextExperiments

    return {
      ...nextExperiments,
      [experimentId]: {
        ...experiment,
        enabled: variationKey !== 'off',
        triggered: true,
        variationKey,
      },
    }
  }, experiments)
}

function getOverriddenChertoExperiments(
  experiments: App.ChertoState['chertoExperiments'],
  overrides: Record<string, string>,
): App.ChertoState['chertoExperiments'] {
  return Object.entries(overrides).reduce<App.ChertoState['chertoExperiments']>((nextExperiments, [experimentId, variationKey]) => ({
    ...nextExperiments,
    [experimentId]: {
      ...nextExperiments[experimentId],
      enabled: variationKey !== 'off',
      experimentId,
      provider: 'cherto',
      triggered: true,
      variationKey,
    },
  }), experiments)
}

function getOverriddenBooleanFlags(
  flags: Record<string, boolean>,
  overrides: Record<string, boolean>,
): Record<string, boolean> {
  return Object.keys(overrides).length === 0 ? flags : { ...flags, ...overrides }
}

function getOverriddenLabFlags(
  flags: App.LabFlags,
  overrides: Record<string, boolean>,
): App.LabFlags {
  return Object.entries(overrides).reduce<App.LabFlags>((nextFlags, [flagKey, enabled]) => ({
    ...nextFlags,
    [flagKey]: {
      ...nextFlags[flagKey],
      enabled,
      key: flagKey,
    },
  }), flags)
}

function getOverriddenLabRemoteConfigFlags(
  flags: Record<string, App.LabRemoteConfigResult>,
  overrides: Record<string, App.LabRemoteConfigResult['value']>,
): Record<string, App.LabRemoteConfigResult> {
  return Object.entries(overrides).reduce<Record<string, App.LabRemoteConfigResult>>((nextFlags, [flagKey, value]) => ({
    ...nextFlags,
    [flagKey]: {
      ...nextFlags[flagKey],
      key: flagKey,
      value,
      error: undefined,
    },
  }), flags)
}

function getSavedExperimentPanelOverrides(state: App.State): ExperimentPanelOverrides | undefined {
  if (!canAccessExperimentPanel(state)) return undefined

  const persistence = getExperimentPanelPersistence()

  const validOverrides: ExperimentPanelOverrides = {
    optimizelyExperiments: getValidOptimizelyExperimentOverrides(state.optimizely.optimizelyExperiments, persistence.overrides.optimizelyExperiments),
    optimizelyFeatureFlags: getValidBooleanOverrides(persistence.overrides.optimizelyFeatureFlags, config.OPTIMIZELY_FEATURE_FLAGS),
    chertoExperiments: getValidChertoExperimentOverrides(persistence.overrides.chertoExperiments),
    labFeatureFlags: getValidBooleanOverrides(persistence.overrides.labFeatureFlags, config.LAB_FEATURE_FLAGS),
    labRemoteConfigFlags: getValidLabRemoteConfigOverrides(persistence.overrides.labRemoteConfigFlags, config.LAB_REMOTE_CONFIG_KEYS),
  }

  if (!hasSameValues(validOverrides.optimizelyExperiments, persistence.overrides.optimizelyExperiments) ||
    !hasSameValues(validOverrides.optimizelyFeatureFlags, persistence.overrides.optimizelyFeatureFlags) ||
    !hasSameValues(validOverrides.chertoExperiments, persistence.overrides.chertoExperiments) ||
    !hasSameValues(validOverrides.labFeatureFlags, persistence.overrides.labFeatureFlags) ||
    !hasSameValues(validOverrides.labRemoteConfigFlags, persistence.overrides.labRemoteConfigFlags)) {
    storeExperimentPanelPersistence({ ...persistence, overrides: validOverrides })
  }

  return Object.values(validOverrides).some(overrides => Object.keys(overrides).length > 0) ? validOverrides : undefined
}

export function getExperimentPanelOverrides(state: App.State): ExperimentPanelOverrides | undefined {
  if (!getExperimentPanelPersistence().enabled) return undefined

  return getSavedExperimentPanelOverrides(state)
}

export function getOptimizelyExperimentOverridesForDecision(
  state: App.State,
  experiments: Record<string, App.OptimizelyExperimentDetails | undefined>,
): Record<string, string> | undefined {
  const overrides = getExperimentPanelOverrides(state)
  if (!overrides) return undefined

  const decisionOverrides = Object.fromEntries(
    Object.entries(experiments).flatMap(([experimentId, experiment]) => {
      const variationKey = overrides.optimizelyExperiments[experimentId]
      if (!experiment || variationKey === undefined || !isValidOptimizelyExperimentOverride(experiment, variationKey)) return []

      return [[experimentId, variationKey]]
    }),
  )
  const invalidExperimentIds = Object.keys(experiments).filter(experimentId => (
    overrides.optimizelyExperiments[experimentId] !== undefined &&
    decisionOverrides[experimentId] === undefined
  ))

  if (invalidExperimentIds.length > 0) {
    const persistence = getExperimentPanelPersistence()
    const invalidExperimentIdSet = new Set(invalidExperimentIds)

    storeExperimentPanelPersistence({
      ...persistence,
      overrides: {
        ...persistence.overrides,
        optimizelyExperiments: Object.fromEntries(
          Object.entries(persistence.overrides.optimizelyExperiments).filter(([experimentId]) => !invalidExperimentIdSet.has(experimentId)),
        ),
      },
    })
  }

  return decisionOverrides
}

export function applyExperimentPanelOverrides(state: App.State, search?: string): App.State {
  const overrides = getSavedExperimentPanelOverrides(state)
  if (!overrides) return state

  // Bootstrap removes router state, so use the current history URL when supplied.
  const preview = search === undefined ? isExperimentPreview(state) : isPreviewQuery(Object.fromEntries(new URLSearchParams(search)))
  const optimizelyExperiments = preview ? {} : getOverriddenOptimizelyExperiments(state.optimizely.optimizelyExperiments, overrides.optimizelyExperiments)
  const optimizelyFeatureFlags = getOverriddenBooleanFlags(state.optimizely.optimizelyFeatureFlags, overrides.optimizelyFeatureFlags)
  const chertoExperiments = preview ? {} : getOverriddenChertoExperiments(state.cherto.chertoExperiments, overrides.chertoExperiments)
  const labFlags = getOverriddenLabFlags(state.system.features.labFlags, overrides.labFeatureFlags)
  const remoteConfigFlags = getOverriddenLabRemoteConfigFlags(state.system.features.remoteConfigFlags, overrides.labRemoteConfigFlags)

  return {
    ...state,
    optimizely: {
      ...state.optimizely,
      optimizelyExperiments,
      optimizelyFeatureFlags,
    },
    cherto: {
      ...state.cherto,
      chertoExperiments,
    },
    system: {
      ...state.system,
      features: {
        ...state.system.features,
        experiments: preview ? {} : getOverriddenOptimizelyExperiments(state.system.features.experiments, overrides.optimizelyExperiments),
        flags: getOverriddenBooleanFlags(state.system.features.flags, overrides.optimizelyFeatureFlags),
        labFlags,
        remoteConfigFlags,
      },
    },
  }
}
